import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Wand2,
  Calculator,
  Mic,
  Images,
  BookUser,
  Megaphone,
  CalendarDays,
  HardHat,
  Package,
  Settings,
  Hammer,
  Link2,
  Truck,
  ShoppingCart,
  ClipboardCheck,
  Mail,
  Banknote,
  FileQuestion,
  DraftingCompass,
  Upload,
  FileDown,
  Building2,
} from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { JOB_STATUS_META } from "@/lib/format";

const PAGES = [
  { to: "/", label: "Today (Dashboard)", icon: LayoutDashboard },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck },
  { to: "/comms", label: "Comms Hub", icon: Mail },
  { to: "/cashflow", label: "Cash Flow", icon: Banknote },
  { to: "/financials", label: "Financials", icon: Banknote },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  { to: "/jobs", label: "Jobs", icon: FolderKanban },
  { to: "/quick-quote", label: "Quick Quote", icon: Wand2 },
  { to: "/boq", label: "Plans & BOQ", icon: Calculator },
  { to: "/renders", label: "Renders", icon: Images },
  { to: "/clients", label: "Clients", icon: BookUser },
  { to: "/crm", label: "Pipeline & CRM", icon: Megaphone },
  { to: "/schedule", label: "Schedule & Gantt", icon: CalendarDays },
  { to: "/deliveries", label: "Deliveries", icon: Truck },
  { to: "/rfis", label: "RFIs", icon: FileQuestion },
  { to: "/consultants", label: "Consultants", icon: DraftingCompass },
  { to: "/contractors", label: "Contractors", icon: HardHat },
  { to: "/suppliers", label: "Suppliers", icon: Package },
  { to: "/settings", label: "Settings", icon: Settings },
];

const ACTIONS = [
  { to: "/jobs?new=1", label: "New job", icon: Hammer },
  { to: "/quick-quote", label: "New quick quote", icon: Wand2 },
  { to: "/boq", label: "Go to Takeoff", icon: Calculator },
  { to: "/field?tab=diary", label: "Site Diary", icon: Mic },
  { to: "/financials?import=1", label: "Import Xero Data", icon: Upload },
  { to: "/financials", label: "View Financial Summary", icon: Banknote },
  { to: "/financials?report=1", label: "Download Profitability Report", icon: FileDown },
  { to: "/settings?xero=1", label: "Xero sync settings", icon: Link2 },
];

/** Global Ctrl/Cmd+K palette: jump to any job or page, run quick actions,
 *  and find any sent email by supplier, subject or job (2+ characters). */
export const CommandPalette = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  // Landlord console only exists for BuiltOS super-admins
  const pages = user?.is_superadmin ? [...PAGES, { to: "/landlord", label: "Landlord console", icon: Building2 }] : PAGES;

  useEffect(() => {
    if (open) {
      api.get("/jobs").then((res) => setJobs(res.data || [])).catch(() => {});
      api.get("/comms/logs", { params: { limit: 200 } }).then((res) => setEmails(res.data.logs || [])).catch(() => {});
    } else {
      setQuery("");
    }
  }, [open]);

  const go = useCallback(
    (to) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange]
  );

  const showEmails = query.trim().length >= 2;

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search jobs, pages, actions, emails…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Jobs">
          {jobs.slice(0, 30).map((j) => {
            const meta = JOB_STATUS_META[j.status];
            return (
              <CommandItem key={j.id} value={`job ${j.name} ${j.client_name || ""}`} onSelect={() => go(`/jobs/${j.id}`)}>
                <FolderKanban className="mr-2 h-4 w-4 text-primary" />
                <span className="truncate">{j.name}</span>
                {meta && <span className="ml-auto text-xs text-muted-foreground">{meta.label}</span>}
              </CommandItem>
            );
          })}
        </CommandGroup>
        {showEmails && emails.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Emails">
              {emails.map((e) => {
                const who = e.to.map((t) => `${t.name} ${t.email}`).join(" ");
                const toLine = e.to.map((t) => t.name || t.email).filter(Boolean).slice(0, 2).join(", ");
                return (
                  <CommandItem
                    key={e.id}
                    value={`email ${e.subject} ${who} ${e.job_name} ${e.type_label}`}
                    onSelect={() => go(`/comms?view=${e.id}`)}
                    data-testid={`command-palette-email-${e.id}`}
                  >
                    <Mail className="mr-2 h-4 w-4 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{e.subject || e.type_label}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {e.type_label}
                        {toLine ? ` · to ${toLine}` : ""}
                        {e.job_name ? ` · ${e.job_name}` : ""}
                      </span>
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
        <CommandSeparator />
        <CommandGroup heading="Quick actions">
          {ACTIONS.map(({ to, label, icon: Icon }) => (
            <CommandItem key={label} value={`action ${label}`} onSelect={() => go(to)}>
              <Icon className="mr-2 h-4 w-4 text-primary" />
              {label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Pages">
          {pages.map(({ to, label, icon: Icon }) => (
            <CommandItem key={to} value={`page ${label}`} onSelect={() => go(to)}>
              <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
